// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

export type CourseHandicapInputs = {
  /** Official WHS Handicap Index (may be negative for plus handicaps). */
  handicapIndex: number;
  /** Tee slope rating (55–155 in WHS; must be > 0). */
  slopeRating: number;
  /** Tee course rating (must be > 0). */
  courseRating: number;
  /** Par for the tee/round. */
  par: number;
  /** 9 or 18. Anything other than 9 is treated as a full 18-hole round. */
  holesCount: number;
};

/**
 * WHS Course Handicap (= Playing Handicap at 100% allowance), rounded to the
 * nearest integer:
 *
 *   Course Handicap = Index × (Slope / 113) + (CourseRating − Par)
 *
 * For 9-hole rounds we only have 18-hole ratings, so the standard approximation
 * is to halve the 18-hole result: (Index/2) × (Slope/113) + (CourseRating − Par)/2.
 *
 * Returns null when any input is missing/invalid (not enough course data), so the
 * caller can leave the handicap blank instead of producing a bogus number.
 *
 * Game-specific handicap allowances (e.g. 95%) are applied downstream — this is
 * the canonical 100% course handicap that gets stored as the player's scp.
 */
export function calculateCourseHandicap(inputs: CourseHandicapInputs): number | null {
  const { handicapIndex, slopeRating, courseRating, par, holesCount } = inputs;
  if (
    !Number.isFinite(handicapIndex) ||
    !Number.isFinite(slopeRating) ||
    slopeRating <= 0 ||
    !Number.isFinite(courseRating) ||
    courseRating <= 0 ||
    !Number.isFinite(par) ||
    par <= 0
  ) {
    return null;
  }

  const fullEighteen = handicapIndex * (slopeRating / 113) + (courseRating - par);
  const value = holesCount === 9 ? fullEighteen / 2 : fullEighteen;
  return Math.round(value);
}

export type ScpFillablePlayer = { id: string; scp: string };
export type TeeRatings = { slopeRating: number; courseRating: number; par: number };

/**
 * Fills an empty playing handicap (scp) for each player from their official
 * handicap index and the selected tee's ratings. Only blanks are touched — a
 * value the user typed (or one prefilled from a prior round) is left untouched.
 *
 * Returns the SAME array reference when nothing changes, so callers can pass the
 * result straight to a state setter without triggering a needless re-render.
 */
export function fillEmptyScpWithCourseHandicap<T extends ScpFillablePlayer>(
  players: T[],
  handicapIndexByPlayerId: Record<string, number>,
  tee: TeeRatings | null | undefined,
  holesCount: number
): T[] {
  if (!tee) return players;

  let changed = false;
  const next = players.map((player) => {
    if (player.scp.trim()) return player;
    const handicapIndex = handicapIndexByPlayerId[player.id];
    if (handicapIndex === undefined) return player;
    const courseHandicap = calculateCourseHandicap({
      handicapIndex,
      slopeRating: tee.slopeRating,
      courseRating: tee.courseRating,
      par: tee.par,
      holesCount,
    });
    if (courseHandicap === null) return player;
    changed = true;
    return { ...player, scp: String(courseHandicap) };
  });

  return changed ? next : players;
}

/**
 * Parses a handicap index that may arrive as a number or a string (the player
 * service stores it as a decimal). Returns null when absent or unparseable.
 */
export function parseHandicapIndex(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
